import type { Quill, RangeStatic } from 'quill';

export const getScrollParent = (
  input: Element | null,
): Promise<Element | null> =>
  new Promise((resolve) => {
    const isShadowDOM = input instanceof ShadowRoot;

    if (!(input instanceof HTMLElement || isShadowDOM)) {
      return resolve(null);
    }

    const node = isShadowDOM ? input.host : input;

    /**
      If either `overflow` or `overflowY` is `auto` or `scroll`, the element
      can be scrolled.
     */

    const { overflow, overflowY } = window.getComputedStyle(node);

    const canScroll = [overflow, overflowY].some((style) =>
      /(auto|scroll)/.test(style),
    );

    /**
      `requestAnimationFrame` is used to delay the execution of the
      `getScroll` function until the next animation frame, allowing the
      scroll position to settle and ensure the `scrollHeight`/`clientHeight`
      comparison is accurate.
     */

    const getScroll = () => {
      /**
        - `clientHeight`: visible height of the element
        - `scrollHeight`: total height of the element including overflow
       */

      const { clientHeight, scrollHeight } = node;

      const hasScroll = scrollHeight > clientHeight;

      resolve(
        canScroll && hasScroll ? node : getScrollParent(node.parentElement),
      );
    };

    requestAnimationFrame(getScroll);
  });

/**
  The purpose of this function is to scroll the cursor into view within the
  Quill editor. It does this by calculating the position of the cursor and
  comparing it with the visible area of the scrollable parent. If the cursor
  is not fully visible, the function scrolls the editor to bring the cursor
  into view.
 */

export const scrollIntoView = async (
  quillEl: Quill,
  selection: RangeStatic,
  toolbarEl: HTMLElement | null,
) => {
  const scrollEl = await getScrollParent(quillEl.root);

  if (scrollEl) {
    const editorPadding = 12;

    /**
      The `top` position of the scrollable parent container. In the
      LiveFeed modal, this is a fixed value relative to the viewport.
     */
    const { top: marginTop } = scrollEl.getBoundingClientRect();

    /**
      The `top` position of the Quill editor component. This value changes
      as the scrollable parent container scrolls relative to the viewport.
     */
    const { top: editorTop } = quillEl.root.getBoundingClientRect();

    /**
      The `bottom` and `top` positions of the cursor or selection. These
      values are measured relative to the scrollable parent container and
      _not_ relative to the viewport.
     */
    const { bottom, top } = quillEl.getBounds(selection.index);

    /**
      The toolbar is part of the editor and floats on top. It is possible for
      the cursor to move behind the toolbar, so we ensure that the parent
      container is scrolled so the editor border, toolbar, and cursor are
      all visible without overlapping.
     */
    const toolbarHeight = toolbarEl?.getBoundingClientRect().height ?? 0;

    /**
      `clientHeight` is the visible height of the scrollable parent container.
      `scrollTop` is the measurement of pixels overflowing obove the visible
      scroll area.
     */
    const { clientHeight, scrollTop } = scrollEl;

    /**
      Because the cursor `top` position is measured relative to the scrollable
      parent container and not the viewport, we need to add the `editorTop` so
      it uses the relative values relative to the viewport.
     */
    const cursorTop = editorTop + top;

    /**
      Measured cursor positions less than this value should cause the
      scrollable parent to scroll up (cause the cursor to move downwards).
     */
    const cursorTopMax = marginTop + editorPadding;

    /**
      Calculate the position of the cursor relative to the viewport and not
      the scrollable parent container.
     */
    const cursorBottom = editorTop + bottom;

    /**
      Add the top measurement of the scrollable parent container to its height
      and then subtract the height of the toolbar and the editor padding. This
      is the maximum position the cursor should be allowed to be otherwise the
      scrollable parent should scroll down (cause the cursor to move upwards).
     */
    const cursorBottomMax =
      marginTop + clientHeight - toolbarHeight - editorPadding;

    if (cursorTop < cursorTopMax) {
      scrollEl.scrollTo({
        behavior: 'smooth',
        top: scrollTop - (cursorTopMax - cursorTop),
      });
    }

    if (cursorBottom > cursorBottomMax) {
      scrollEl.scrollTo({
        behavior: 'smooth',
        top: scrollTop + cursorBottom - cursorBottomMax,
      });
    }
  }
};
